// Who asked a member question, for which city, when, and how long it has
// waited (2026-11-03; backend admin.vaultQuestionController withAskerFields).
// The list draws AskedByInline next to the "member submitted" tag and
// AskedByTimes under it. The question's View page draws MemberQuestionInfo,
// labelled rows (founder 2026-11-04), and the Edit page AskedByBlock, so the
// pencil shows who asked too.
import React from "react";
import { Link } from "react-router-dom";
import { formatEt } from "../User/introPauseUtils";
import {
  askerLabel,
  askerPagePath,
  askedAtLine,
  waitingLine,
  internalNoteLine,
  approvedWords,
  shownToCityWords,
  answeredWords,
} from "./memberQuestionUtils";

// The name opens the member's Admin page; a deleted account's is plain text.
const AskerLink = ({ item }) => {
  const path = askerPagePath(item);
  const name = askerLabel(item);
  return path ? (
    <Link to={path} className="text-decoration-underline" style={{ whiteSpace: "nowrap" }} title="Open this member">
      {name}
    </Link>
  ) : (
    <span style={{ whiteSpace: "nowrap" }}>{name}</span>
  );
};

const Waiting = ({ item }) => {
  const waiting = waitingLine(item);
  if (!waiting) return null;
  return <div className={`text-sm fw-medium ${waiting.late ? "text-danger-600" : "text-secondary-light"}`}>{waiting.text}</div>;
};

/** "· Nicole (..1b143b) · Ottawa" */
export const AskedByInline = ({ item }) => {
  if (!item || !item.askedBy) return null;
  return (
    <span className="text-sm text-secondary-light">
      {" · "}
      <AskerLink item={item} />
      {item.askedPocketName ? ` · ${item.askedPocketName}` : ""}
    </span>
  );
};

/** "Asked Oct 18, 11:11 PM ET", then "Waiting 16 days" while it waits. */
export const AskedByTimes = ({ item }) => {
  if (!item || !item.askedBy) return null;
  const asked = askedAtLine(item);
  return (
    <>
      {asked ? (
        <div className="text-sm text-secondary-light" style={{ marginTop: 4 }}>
          {asked}
        </div>
      ) : null}
      <Waiting item={item} />
    </>
  );
};

/** The View page: "Asked by: Nicole (..1b143b), Ottawa, Oct 18, 11:11 PM ET", the wait, the internal note. */
export const AskedByBlock = ({ item }) => {
  if (!item || !item.askedBy) return null;
  const when = formatEt(item.askedAt);
  const note = internalNoteLine(item);
  return (
    <div className="col-md-12 mb-3">
      <label className="form-label fw-bold">Asked by:</label>
      <div>
        <AskerLink item={item} />
        {item.askedPocketName ? `, ${item.askedPocketName}` : ""}
        {when ? `, ${when}` : ""}
      </div>
      <Waiting item={item} />
      {/* Never the untrue "Publish to run it" note (memberQuestionUtils). */}
      {note ? (
        <div className="text-sm text-secondary-light" style={{ marginTop: 4 }}>
          Internal note: {note}
        </div>
      ) : null}
    </div>
  );
};

// "Club" beside the name while the member's Club is live now (backend
// askedByClub). Asking needs the Club, so a lapse since shows no pill.
const ClubPill = () => (
  <span
    className="bg-lilac-100 text-lilac-600 fw-semibold"
    style={{ fontSize: 12, borderRadius: 999, padding: "2px 8px", marginLeft: 6, whiteSpace: "nowrap" }}
    title="Their Curiouzz Club is live now"
  >
    Club
  </span>
);

const Field = ({ label, children }) => (
  <div className="col-md-4 mb-3">
    <label className="form-label fw-bold">{label}</label>
    {children}
  </div>
);

/**
 * The View page, at the top of Question Info (founder 2026-11-04): a
 * "Member question" heading, then Asked by (with the Club pill), City,
 * Asked, Approved, Shown to the city and, once shown, Answered with a link
 * to the Stats tab. Nothing on a bank question. onSeeStats opens the tab.
 */
export const MemberQuestionInfo = ({ item, onSeeStats }) => {
  if (!item || !item.askedBy) return null;
  const note = internalNoteLine(item);
  const shown = shownToCityWords(item);
  const answered = answeredWords(item);
  return (
    <>
      <div className="col-md-12 mb-12">
        <h6 className="text-md fw-semibold mb-0">Member question</h6>
      </div>
      <Field label="Asked by:">
        <div>
          <AskerLink item={item} />
          {item.askedByClub === true ? <ClubPill /> : null}
        </div>
      </Field>
      <Field label="City:">
        <div>{item.askedPocketName || "N/A"}</div>
      </Field>
      <Field label="Asked:">
        <div>{formatEt(item.askedAt) || "N/A"}</div>
      </Field>
      <Field label="Approved:">
        <div>{approvedWords(item)}</div>
        <Waiting item={item} />
      </Field>
      {shown !== null ? (
        <Field label="Shown to the city:">
          <div>{shown}</div>
        </Field>
      ) : null}
      {answered !== null ? (
        <Field label="Answered:">
          <div>
            {answered ? `${answered} · ` : ""}
            {onSeeStats ? (
              <button
                type="button"
                className="btn btn-link p-0 text-lilac-600 text-decoration-underline align-baseline"
                style={{ fontSize: "inherit" }}
                onClick={onSeeStats}
              >
                See Stats
              </button>
            ) : null}
          </div>
        </Field>
      ) : null}
      {/* Never the untrue "Publish to run it" note (memberQuestionUtils). */}
      {note ? <div className="col-md-12 mb-3 text-sm text-secondary-light">Internal note: {note}</div> : null}
      <div className="col-md-12 mb-3">
        <div style={{ borderTop: "1px solid var(--neutral-200)" }} />
      </div>
    </>
  );
};
